import { useGlobalFilter, useRowSelect, useSortBy, useTable } from 'react-table'
import clsx from 'clsx'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, CircularProgress } from '@material-ui/core'
import { isEqual } from 'lodash'
import SydInput from '../../../../../../../commonDesign/SydInput'
import Icon from '../../../../../../../atoms/Icon'
import { ICON_NAMES } from '../../../../../../../../constants'
import SydLabel from '../../../../../../../commonDesign/SydLabel'
import SydSelect from '../../../../../../../commonDesign/SydSelect'
import SydButton from '../../../../../../../commonDesign/Button'
import { useStyles } from './styles'

export const UserSelectTable = ({
  data,
  isLoading,
  hasSearch = true,
  hasFilter = true,
  selectedCount = 0,
  columns,
  onSelectChange
}) => {
  const classes = useStyles()
  const [tableFilters, setTableFilters] = useState({
    userTypeId: null
  })

  const filteredData = useMemo(() => {
    if (!Object.keys(tableFilters).length) return data

    return data.filter((row) => {
      return Object.entries(tableFilters).every(([key, value]) => {
        if (value === null) return true
        if (typeof value === 'string') {
          return row[key].toLowerCase().includes(value.toLowerCase())
        }
        if (Array.isArray(value)) {
          return value.includes(row[key])
        }
        return row[key] === value
      })
    })
  }, [data, tableFilters])

  const {
    getTableProps,
    headerGroups,
    rows,
    getTableBodyProps,
    prepareRow,
    setGlobalFilter,
    selectedFlatRows
  } = useTable({
    columns,
    data: filteredData ?? []
  }, useGlobalFilter, useSortBy, useRowSelect)

  const selectedExternalIds = useRef([])

  const handleSelectChange = useCallback((externalIds) => {
    onSelectChange?.(externalIds)
  }, [onSelectChange])

  useEffect(() => {
    const selectedRows = [...new Set(selectedFlatRows.map((row) => row.original.externalUserId))]
    if (!isEqual(selectedRows, selectedExternalIds.current)) {
      selectedExternalIds.current = selectedRows
      handleSelectChange(selectedExternalIds.current)
    }
  }, [handleSelectChange, selectedFlatRows])

  if (isLoading || !columns?.length) {
    return (
      <CircularProgress />
    )
  }

  if (!data || !columns?.length) {
    return ''
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <div className={classes.filters}>
          {hasSearch && (
            <SydLabel label='Search for user'>
              <SydInput
                size='sm'
                className={classes.search}
                placeholder='Search'
                onChange={(e) => setGlobalFilter(e.target.value)}
                startAdornment={<Icon name={ICON_NAMES.search} />}
              />
            </SydLabel>
          )}
          {hasFilter && (
            <SydLabel
              label={(
                <Box display='flex' gap={1} justifyContent='space-between'>
                  Select User Type to filter to
                  {!!tableFilters?.userTypeId && (
                    <SydButton
                      size='xs'
                      onClick={() => {
                        setTableFilters((prev) => ({ ...prev, userTypeId: null }))
                      }}
                      style={{ marginTop: '-2px' }}
                    >
                      View all types
                    </SydButton>
                  )}
                </Box>
              )}
            >
              <SydSelect
                size='sm'
                className={classes.search}
                placeholder='Select User Type'
                value={tableFilters.userTypeId}
                options={[
                  { value: 1, label: 'Wealth Owner' },
                  { value: 2, label: 'Advisors' }
                ]}
                onChange={(e) => {
                  setTableFilters((prev) => ({ ...prev, userTypeId: e.target.value }))
                }}
              />
            </SydLabel>
          )}
        </div>
        <div>
          {selectedCount} User{selectedCount !== 1 ? 's' : ''} selected
        </div>
      </div>
      <div className={classes.tableWrapper}>
        <table {...getTableProps()} className={classes.table}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr key={headerGroup.key} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th key={column.key} {...column.getHeaderProps(column.getSortByToggleProps())}>
                    <div className='__thContainer'>
                      <span>{column.render('Header')}</span>
                      <span className='__sort-icon'>
                        {column.isSorted ? column.isSortedDesc ? <Icon name={ICON_NAMES.down} /> : <Icon name={ICON_NAMES.up} /> : ''}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row, i) => {
              prepareRow(row)
              return (
                <tr
                  {...row.getRowProps()}
                  className={clsx(
                    classes.tableRow,
                    {
                      __selected: (row.isSelected)
                    })}
                  key={row.original?.externalUserId}
                >
                  {row.cells.map((cell, index) => (
                    <td
                      key={cell.key}
                      {...cell.getCellProps()}
                      className={clsx(classes.tableCell)}
                    >
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

UserSelectTable.propTypes = {
  data: PropTypes.array,
  isLoading: PropTypes.bool,
  hasSearch: PropTypes.bool,
  hasFilter: PropTypes.bool,
  selectedCount: PropTypes.number,
  columns: PropTypes.array,
  onSelectChange: PropTypes.func
}
